import { getApiClient } from '@signalvote/api-client';
import type { CampaignQuestionDto, CreateQuestionRequest, UpdateQuestionRequest } from '@signalvote/shared';
import { defineQuery, defineWrite, edit, requireSelectedCampaign } from '@/lib/data';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

export type CreateQuestionData = Pick<CreateQuestionRequest, 'text' | 'type'> & Partial<CreateQuestionRequest>;

export interface UpdateQuestionData {
  questionId: string;
  updates: Partial<UpdateQuestionRequest>;
}

function selectedCampaignId(): string {
  return useCampaignStore.getState().selectedCampaign!.id;
}

/**
 * The selected campaign's active survey questions. Every question write saves through the
 * request's `TenantDbContext`, so its write set names CampaignQuestion and refreshes this read.
 */
export const useCampaignQuestions = defineQuery({
  key: () => ['campaignQuestions', 'list'],
  reads: 'GET /api/campaigns/{id}/questions',
  fetch: (_: undefined, { campaignId }): Promise<CampaignQuestionDto[]> => {
    requireSelectedCampaign(campaignId);
    return api.tenantCampaigns.getQuestions(campaignId!, { activeOnly: true });
  },
});

export const useCreateQuestion = defineWrite({
  send: (data: CreateQuestionData) =>
    api.tenantCampaigns.createQuestion(selectedCampaignId(), { options: null, displayOrder: null, ...data }),
});

export const useUpdateQuestion = defineWrite({
  send: ({ questionId, updates }: UpdateQuestionData) =>
    api.tenantCampaigns.updateQuestion(selectedCampaignId(), questionId, {
      text: null,
      type: null,
      options: null,
      displayOrder: null,
      isActive: null,
      ...updates,
    }),
});

/** Deactivate a question. It leaves the list on click and comes back if the server refuses. */
export const useDeactivateQuestion = defineWrite({
  send: (questionId: string) => api.tenantCampaigns.deleteQuestion(selectedCampaignId(), questionId),
  patch: (questionId) => [edit(useCampaignQuestions, undefined, (old) => old.filter((q) => q.id !== questionId))],
});
